import { SecureDocumentList } from "@/components/documents/SecureDocumentList";
import { SecureDocumentUpload } from "@/components/documents/SecureDocumentUpload";
import { StorageUsage } from "@/components/documents/StorageUsage";
import { api } from "@/services/api";
import { i18nToast } from "@/utils/i18nToast";
import { Files, FileText, Info, Shield, Upload } from "lucide-react";
import React, { useState } from "react";
import { PageBanner } from "../../components/ui/PageBanner";
import type { StoredDocument } from "@/types/document";

// The subset of the server's upload allowlist a browser can display itself.
// HEIC is accepted on upload but not listed: most browsers cannot show it, so
// it is downloaded like any other file.
const INLINE_VIEWABLE_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
]);

const DocumentsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"upload" | "documents">(
    "documents",
  );
  const [documents, setDocuments] = useState<StoredDocument[]>([]);

  const handleUploadComplete = (newDocuments: StoredDocument[]) => {
    setDocuments((prev) => [...newDocuments, ...prev]);
    setActiveTab("documents");
  };

  const handleDocumentDelete = (documentId: string) => {
    setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
  };

  const handleDocumentSelect = async (document: StoredDocument) => {
    // Open a tab immediately in direct click context to avoid popup blockers.
    // Without the "noopener" feature: with it, window.open returns null by
    // specification, so the blank tab was orphaned and the file opened in a
    // second one. The opener is cut by hand instead — the tab only ever holds
    // our own blob, but it has no reason to reach back into this window.
    const openedTab = window.open("about:blank", "_blank");
    if (openedTab) openedTab.opener = null;

    try {
      const response = await api.get(`/documents/${document.id}/download`, {
        responseType: "blob",
      });

      const received = response.data as Blob;
      // A blob: URL belongs to the app's origin, so whatever it holds runs as
      // the app when it is opened as a page. Only types a browser shows as a
      // document or picture — never as script — are opened in a tab;
      // anything else, including legacy uploads stored as text/html or SVG,
      // is saved to disk instead.
      if (!INLINE_VIEWABLE_TYPES.has(received.type.toLowerCase())) {
        if (openedTab && !openedTab.closed) openedTab.close();
        const safe = new Blob([received], { type: "application/octet-stream" });
        const downloadUrl = window.URL.createObjectURL(safe);
        const a = window.document.createElement("a");
        a.href = downloadUrl;
        a.download = document.originalName || "document";
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
        setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 30000);
        return;
      }

      const blob = received;
      const blobUrl = window.URL.createObjectURL(blob);
      if (openedTab) {
        openedTab.location.href = blobUrl;
      } else {
        // Popup-blocker fallback
        const a = window.document.createElement("a");
        a.href = blobUrl;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
      }

      // Delay revoke so browser tab can resolve the blob URL.
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
    } catch (error: any) {
      console.error("Open document failed:", error);
      if (openedTab && !openedTab.closed) {
        openedTab.close();
      }
      i18nToast.error("Open failed: " + (error?.message || "Unknown error"));
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Page Banner */}
      <PageBanner
        icon={FileText}
        title="Documents"
        description="Manage your medical documents securely and encrypted"
      />

      <StorageUsage />

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-3 py-3 sm:h-16 sm:flex-row sm:items-center sm:justify-between sm:py-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 shrink-0 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Files className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">
                    Documents
                  </h1>
                  <p className="text-sm text-gray-600">
                    Secure management of your health documents
                  </p>
                </div>
              </div>

              {/* Tab Navigation */}
              <nav className="flex shrink-0 space-x-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab("documents")}
                  className={`whitespace-nowrap px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === "documents"
                      ? "bg-white text-primary-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <Files className="w-4 h-4 inline-block mr-2" />
                  My Documents
                </button>
                <button
                  onClick={() => setActiveTab("upload")}
                  className={`whitespace-nowrap px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === "upload"
                      ? "bg-white text-primary-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <Upload className="w-4 h-4 inline-block mr-2" />
                  Upload
                </button>
              </nav>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Security Info Banner */}
          <div className="mb-6 bg-gradient-to-r from-green-50 to-primary-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-green-800 mb-1">
                  Highest security standards
                </h3>
                <p className="text-sm text-green-700">
                  Documents travel over an encrypted connection and are stored
                  privately on our servers. Only you, and the people you share
                  them with, can open them.
                </p>
              </div>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === "upload" ? (
            <div className="space-y-6">
              <SecureDocumentUpload
                onUploadComplete={handleUploadComplete}
                maxFiles={10}
                maxFileSize={100}
                defaultPrivate={true}
              />

              {/* Upload Guidelines */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-primary-600 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      Document upload guidelines
                    </h3>
                    <div className="space-y-3 text-sm text-gray-600">
                      <div>
                        <h4 className="font-medium text-gray-800">
                          Supported file formats:
                        </h4>
                        <p>PDF, JPEG, PNG, WebP, HEIC</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-800">
                          Maximum file size:
                        </h4>
                        <p>100 MB per file, up to 10 files at once</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-800">
                          Categories:
                        </h4>
                        <p>
                          Categorize documents as health record, vaccinations,
                          prescriptions, insurance, travel documents, or other.
                        </p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-800">Privacy:</h4>
                        <p>
                          Private documents are encrypted automatically and
                          visible only to you. Public documents can be shared
                          with doctors or other providers.
                        </p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-800">Tags:</h4>
                        <p>
                          Use tags for better organization and faster document
                          search.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <SecureDocumentList
              documents={documents}
              onDocumentSelect={handleDocumentSelect}
              onDocumentDelete={handleDocumentDelete}
              viewMode="list"
              allowViewModeToggle={false}
              showUpload={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentsPage;
